import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, Layers, Settings2, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "./lib/api";
import GroupOrderCard from "./GroupOrderCard";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;

/** OPS-5B — the group order monitor. The app finds the groups across every job; people approve
 *  them; the saving is measured. */
export default function GroupOrders() {
  const [cands, setCands] = useState(null);
  const [groups, setGroups] = useState(null);
  const [savings, setSavings] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const [picked, setPicked] = useState({});
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, g, s] = await Promise.all([opsApi.groupCandidates(), opsApi.groupOrders(),
        opsApi.groupSavings()]);
      setCands(c); setGroups(g); setSavings(s); setCfg(c.settings);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load group orders"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rescan = async () => {
    setBusy(true);
    try {
      const snap = await opsApi.groupMonitorRun();
      toast.success(snap.skipped
        || `${snap.eligible} group(s) worth ${money(snap.projected_saving)} in projected saving`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "The scan failed"); }
    finally { setBusy(false); }
  };

  const build = async (c) => {
    const chosen = picked[c.key] || c.jobs.map((j) => j.project_id);
    setBusy(true);
    try {
      const g = await opsApi.groupCreate({
        manufacturer: c.manufacturer, project_ids: chosen,
        authorization_note: (notes[c.key] || "").trim(),
      });
      toast.success(`${g.group_po_number} built — request the quote when you are ready`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That group could not be built"); }
    finally { setBusy(false); }
  };

  const saveCfg = async (patch) => {
    try { setCfg(await opsApi.groupSettingsPatch(patch)); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That setting did not save"); }
  };

  if (!cands || !groups) {
    return <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Scanning every job for group buys…
    </div>;
  }
  const eligible = cands.candidates.filter((c) => c.eligible);
  const rest = cands.candidates.filter((c) => !c.eligible);

  return (
    <div className="space-y-4" data-testid="group-orders">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[["Groups ready to buy", `${eligible.length}`, "group-eligible"],
          ["Projected saving", money(eligible.reduce((t, c) => t + c.projected_saving, 0)),
            "group-projected"],
          ["Saved so far", money(savings?.total_saving), "group-saved"],
          ["Live group orders", `${groups.summary.groups}`, "group-live"]]
          .map(([label, value, tid]) => (
            <div key={label} className="rounded-lg border border-slate-200 bg-white p-4"
                 data-testid={tid}>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
              <p className="stat-num text-2xl font-semibold text-slate-900">{value}</p>
            </div>
          ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-slate-500">
          Scans every eligible job for unordered material, grouped by manufacturer. Send-by is the
          earliest order-by date in the group — no job is ever delayed past its own.
        </p>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowCfg((v) => !v)}
                  data-testid="group-settings-toggle">
            <Settings2 className="mr-1.5 h-3.5 w-3.5" /> Settings
          </Button>
          <Button size="sm" variant="outline" onClick={rescan} disabled={busy}
                  data-testid="group-rescan">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Re-scan now
          </Button>
        </div>
      </div>

      {showCfg && cfg && (
        <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4"
             data-testid="group-settings">
          <label className="block text-xs">
            <span className="text-slate-500">Required-on-site window (days)</span>
            <Input type="number" defaultValue={cfg.window_days} className="mt-1 h-8 text-xs"
                   data-testid="group-window-days"
                   onBlur={(e) => saveCfg({ window_days: Number(e.target.value) })} />
          </label>
          <label className="block text-xs">
            <span className="text-slate-500">Minimum combined value</span>
            <Input type="number" defaultValue={cfg.min_value} className="mt-1 h-8 text-xs"
                   data-testid="group-min-value"
                   onBlur={(e) => saveCfg({ min_value: Number(e.target.value) })} />
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={cfg.require_submittal}
                   onChange={(e) => saveCfg({ require_submittal: e.target.checked })}
                   data-testid="group-require-submittal" />
            Require an approved submittal
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={cfg.allow_ready}
                   onChange={(e) => saveCfg({ allow_ready: e.target.checked })}
                   data-testid="group-allow-ready" />
            Let Ready jobs join (with a written authorization)
          </label>
        </div>
      )}

      <div className="space-y-3" data-testid="group-candidates">
        {eligible.length === 0 && (
          <p className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600">
            No group buys right now. A group needs two or more jobs with unordered material from
            the same manufacturer, inside the window and over the minimum.
          </p>
        )}
        {eligible.map((c) => {
          const chosen = picked[c.key] || c.jobs.map((j) => j.project_id);
          const needsNote = c.jobs.some((j) => chosen.includes(j.project_id)
            && j.needs_authorization);
          return (
            <div key={c.key} className="rounded-xl border border-emerald-200 bg-white p-4"
                 data-testid={`group-candidate-${c.manufacturer}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-slate-900">{c.manufacturer}</span>
                <span className="text-xs text-slate-500">
                  {c.jobs.length} jobs · {c.line_count} line(s) · lead {c.lead_time_days}d
                </span>
                {c.send_by && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
                    send by {c.send_by}
                  </span>
                )}
                {c.past_window_lines > 0 && (
                  <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] text-rose-700">
                    {c.past_window_lines} line(s) already past their order-by
                  </span>
                )}
                {c.projected_saving > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                    <TrendingDown className="h-3 w-3" /> {money(c.projected_saving)} projected
                    ({c.discount_pct}% break)
                  </span>
                )}
                <span className="ml-auto text-sm font-semibold text-slate-900">
                  {money(c.value)}
                </span>
              </div>

              <div className="mt-2 space-y-1">
                {c.jobs.map((j) => (
                  <label key={j.project_id} className="flex flex-wrap items-center gap-2 text-xs">
                    <input type="checkbox" checked={chosen.includes(j.project_id)}
                           data-testid={`group-job-${c.manufacturer}-${j.job_number}`}
                           onChange={(e) => setPicked((p) => ({
                             ...p,
                             [c.key]: e.target.checked
                               ? [...chosen, j.project_id]
                               : chosen.filter((x) => x !== j.project_id),
                           }))} />
                    <span className={`h-2 w-2 rounded-full ${j.frs_review_required ? "bg-rose-500"
                      : j.needs_authorization ? "bg-amber-500" : "bg-emerald-500"}`} />
                    <span className="font-medium">{j.job_number}</span>
                    <span className="text-slate-600">{j.name}</span>
                    <span className="text-slate-400">
                      {j.lines} line(s) · {money(j.value)}
                      {j.needs_authorization ? ` · ${j.ops_status}, needs authorization` : ""}
                      {j.frs_review_required ? " · FRS needs review" : ""}
                    </span>
                  </label>
                ))}
              </div>

              {needsNote && (
                <Input value={notes[c.key] || ""}
                       onChange={(e) => setNotes((n) => ({ ...n, [c.key]: e.target.value }))}
                       placeholder="Who authorized buying on a job that is not Open"
                       className="mt-2 h-8 text-xs" data-testid={`group-note-${c.manufacturer}`} />
              )}

              <div className="mt-3 flex justify-end">
                <Button size="sm" disabled={busy || chosen.length < 1} onClick={() => build(c)}
                        data-testid={`group-build-${c.manufacturer}`}>
                  <Layers className="mr-1.5 h-3.5 w-3.5" />
                  Build group order for {chosen.length} job(s)
                </Button>
              </div>
            </div>
          );
        })}

        {rest.length > 0 && (
          <details className="rounded-xl border border-slate-200 bg-white p-4"
                   data-testid="group-not-yet">
            <summary className="cursor-pointer text-xs font-medium text-slate-600">
              {rest.length} manufacturer(s) not groupable yet — and why
            </summary>
            <ul className="mt-2 space-y-1 text-xs text-slate-600">
              {rest.map((c) => (
                <li key={c.key}>
                  <b>{c.manufacturer}</b> · {money(c.value)} · {c.why_not}
                </li>
              ))}
            </ul>
            {cands.skipped.length > 0 && (
              <>
                <p className="mt-3 text-xs font-medium text-slate-600">
                  Lines held back ({cands.skipped.length})
                </p>
                <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
                  {cands.skipped.slice(0, 12).map((s, i) => (
                    <li key={i}>{s.job_number} · {s.manufacturer}
                      {s.part_number ? ` · ${s.part_number}` : ""} — {s.reason}</li>
                  ))}
                </ul>
              </>
            )}
          </details>
        )}
      </div>

      <div className="space-y-3" data-testid="group-order-list">
        {groups.group_orders.map((g) => (
          <GroupOrderCard key={g.id} group={g} onChanged={load} />
        ))}
      </div>

      {savings?.by_manufacturer?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="group-savings">
          <p className="text-xs font-semibold text-slate-700">
            Measured saving · {money(savings.total_saving)} across {savings.groups} group order(s)
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
            {savings.by_manufacturer.map((m) => (
              <li key={m.manufacturer}>
                {m.manufacturer} — {money(m.saving)} on {money(m.budgeted)} budgeted
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
